import { useState, useEffect, useMemo, useCallback, useContext } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { useRouterActions } from 'react-resource-router';

import { Notifications } from '@atlaskit/atlassian-navigation';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { NotificationIndicator } from '@atlaskit/notification-indicator';
import { NotificationLogClient } from '@atlaskit/notification-log-client';
import { TriggerProps, Popup } from '@atlaskit/popup';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import NotificationList from '@atlassian/notification-list';

import EmptyNotificationLogClient from './empty-notification-log-client';
import { HorizontalNavContext } from './horizontal-nav-context';

const NOTIFICATION_LOG_URL = '/gateway/api/notification-log';
const CLEAR_BADGE_TIME = 10000;
const REFRESH_INTERVAL = 180000; // 3 minutes

const PopupContent = styled.div({
  height: 'calc(100vh - 100px)',
  width: '540px',
  maxWidth: '100vw',
});

const PlatformNotifications = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isCleared, setIsCleared] = useState(false);
  const { push } = useRouterActions();
  const enableClientSideLinking = FeatureGates.checkGate(
    StatsigFeatureKeys.enableClientSideLinking
  );

  const { currentWorkspace, defaultWorkspaceSlug } =
    useContext(HorizontalNavContext);
  const workspaceSlug = currentWorkspace?.slug || defaultWorkspaceSlug;

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isCleared) {
      const timeout = setTimeout(() => {
        setIsCleared(false);
      }, CLEAR_BADGE_TIME);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isCleared]);

  const handleClick = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
      setIsCleared(true);
    }
  }, [isOpen]);

  const notificationLogClient = useMemo(
    // @ts-ignore
    () => new NotificationLogClient(NOTIFICATION_LOG_URL, undefined),
    []
  );

  const NotificationsBadge = useMemo(
    // eslint-disable-next-line react/display-name
    () => () =>
      (
        <NotificationIndicator
          notificationLogProvider={Promise.resolve(
            isCleared || isOpen
              ? new EmptyNotificationLogClient()
              : notificationLogClient
          )}
          refreshRate={REFRESH_INTERVAL}
          refreshOnHidden
        />
      ),
    [isOpen, isCleared, notificationLogClient]
  );

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const handleClientLinks = (e: React.MouseEvent) => {
    const { href } = e.target as HTMLAnchorElement;
    if (href && !(e.metaKey || e.ctrlKey)) {
      e.preventDefault();

      const { origin, pathname } = new URL(href);
      if (origin === window.location.origin) {
        // Do not do client-side transition if notification points to different workspace context
        // Verify this by comparing workspace slugs in pathname (first path segment)
        if (
          (workspaceSlug && pathname.startsWith(`/${workspaceSlug}/`)) ||
          pathname.startsWith(`/account/workspaces`)
        ) {
          push(href);
        } else {
          window.open(href, '_parent');
        }
      } else {
        window.open(href, '_blank');
      }

      setIsOpen(false);
    }
  };

  // Renders list of notifications in Popup
  const NotifList = () => (
    <PopupContent
      data-testid="platform-notif-popup-content"
      onClick={enableClientSideLinking ? handleClientLinks : undefined}
    >
      <NotificationList />
    </PopupContent>
  );

  // includes bell icon with unseen notification count
  const NotifBellWithCount = useMemo(
    () =>
      (triggerProps: TriggerProps): JSX.Element => {
        return (
          <Notifications
            testId="platform-notif-bell"
            tooltip="Notifications"
            onClick={handleClick}
            isSelected={isOpen}
            badge={NotificationsBadge}
            {...triggerProps}
          />
        );
      },
    [isOpen, handleClick, NotificationsBadge]
  );

  return (
    <Popup
      placement="bottom-end"
      content={NotifList}
      trigger={NotifBellWithCount}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

export default PlatformNotifications;
